import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Form } from 'antd';

import type { SearchPatternsParams } from '@/entities/pattern';

import { HelpPopover } from '@/shared/cms/question-mark';
import { useAppDispatch } from '@/shared/lib/store';
import { Button } from '@/shared/ui/button';
import { InputMinMax, InputRadioGroup, InputSelect } from '@/shared/ui/input';

import { useFilterOptions } from '../../api/use-filter-options';
import { usePatternFilterValues } from '../../model/selectors/pattern-filter';
import {
    clearFilter,
    setFilterValues,
} from '../../model/slices/pattern-filter.slice';

import cls from './pattern-filter-form.module.css';

interface PatternFilterFormProps {
    resetKey?: number;
    afterSubmit?: () => void;
}

export const PatternFilterForm = memo(
    ({ resetKey = 0, afterSubmit }: PatternFilterFormProps) => {
        const { t } = useTranslation('pattern');
        const dispatch = useAppDispatch();
        const filterValues = usePatternFilterValues();

        // Retrieve dynamic options
        const {
            isLoading,
            tickerOptions,
            timeframeOptions,
            lengthOptions,
            thresholdOptions,
            directionOptions,
        } = useFilterOptions(t);

        // Create form
        const [form] = Form.useForm<SearchPatternsParams>();

        // Local state to force rendering after a reset
        const [formKey, setFormKey] = useState(0);

        useEffect(() => {
            form.setFieldsValue(filterValues.filters);
        }, [form, filterValues]);

        // Effect to reset form when resetKey changes
        useEffect(() => {
            if (resetKey > 0) {
                form.resetFields();
                setFormKey((prev) => prev + 1);
            }
        }, [resetKey, form]);

        // Constants for repeated strings
        const maxPercentPlaceholder = t('filters.max_percent');
        const minPercentPlaceholder = t('filters.min_percent');

        const handleSubmit = useCallback(
            (values: SearchPatternsParams) => {
                dispatch(setFilterValues(values));

                if (afterSubmit) afterSubmit();
            },
            [afterSubmit, dispatch],
        );

        const handleReset = useCallback(() => {
            form.resetFields();
            // Force re-rendering to update InputMinMax components
            setFormKey((prev) => prev + 1);
            dispatch(clearFilter());

            // Trigger search with empty values
            if (afterSubmit) afterSubmit();
        }, [dispatch, form, afterSubmit]);

        return (
            <Form<SearchPatternsParams>
                key={formKey}
                className={cls.root}
                disabled={isLoading}
                form={form}
                layout="horizontal"
                onFinish={handleSubmit}
            >
                <Form.Item
                    className={cls.input}
                    label={t('filters.Ticker')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_TICKER" />
                        <Form.Item
                            name="Ticker"
                            noStyle
                        >
                            <InputSelect
                                allowClear
                                isBlock
                                isMultiple
                                isSorted
                                options={tickerOptions}
                                placeholder={t('filters.selectTicker')}
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.Timeframe')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_TIMEFRAME" />
                        <Form.Item
                            name="Timeframe"
                            noStyle
                        >
                            <InputSelect
                                allowClear
                                isBlock
                                isMultiple
                                options={timeframeOptions}
                                placeholder={t('filters.selectTimeframe')}
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.pattern_len_')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_LENGTH" />
                        <Form.Item
                            name="pattern_len_"
                            noStyle
                        >
                            <InputSelect
                                allowClear
                                isBlock
                                isMultiple
                                isSorted
                                options={lengthOptions}
                                placeholder={t('filters.selectPattern_len_')}
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.price_threshold_post_mean')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_THRESHOLD" />
                        <Form.Item
                            name="price_threshold_post_mean"
                            noStyle
                        >
                            <InputSelect
                                allowClear
                                isBlock
                                isMultiple
                                isSorted
                                options={thresholdOptions}
                                placeholder={t(
                                    'filters.selectPrice_threshold_post_mean',
                                )}
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.frequency')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_FREQUENCY" />
                        <Form.Item noStyle>
                            <InputMinMax
                                isBlock
                                maxName="pattern_index_count_max"
                                maxPlaceholder={t('filters.max')}
                                minName="pattern_index_count_min"
                                minPlaceholder={t('filters.min')}
                                step="1"
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.price_drawdown_post_mean')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_DRAWDOWN" />
                        <Form.Item noStyle>
                            <InputMinMax
                                isBlock
                                maxName="price_drawdown_post_mean_max"
                                maxPlaceholder={maxPercentPlaceholder}
                                minName="price_drawdown_post_mean_min"
                                minPlaceholder={minPercentPlaceholder}
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.efficiency')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_EFFICIENCY" />
                        <Form.Item noStyle>
                            <InputMinMax
                                isBlock
                                maxName="price_reg_post_mean_max"
                                maxPlaceholder={maxPercentPlaceholder}
                                minName="price_reg_post_mean_min"
                                minPlaceholder={minPercentPlaceholder}
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.perc_rialzista')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_RIALZISTA" />
                        <Form.Item noStyle>
                            <InputMinMax
                                isBlock
                                maxName="perc_max_rialzista"
                                maxPlaceholder={maxPercentPlaceholder}
                                minName="perc_min_rialzista"
                                minPlaceholder={minPercentPlaceholder}
                                step="1"
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.perc_ribassista')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_RIBASSISTA" />
                        <Form.Item noStyle>
                            <InputMinMax
                                isBlock
                                maxName="perc_max_ribassista"
                                maxPlaceholder={maxPercentPlaceholder}
                                minName="perc_min_ribassista"
                                minPlaceholder={minPercentPlaceholder}
                                step="1"
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <Form.Item
                    className={cls.input}
                    label={t('filters.direction_type')}
                >
                    <Flex
                        align="center"
                        gap={16}
                    >
                        <HelpPopover helpId="PATTERN_FILTER_DIRECTION" />
                        <Form.Item
                            name="direction_type"
                            noStyle
                        >
                            <InputRadioGroup
                                buttonStyle="solid"
                                defaultValue=""
                                isBlock
                                options={directionOptions}
                                optionType="button"
                            />
                        </Form.Item>
                    </Flex>
                </Form.Item>

                <div className={cls.buttons}>
                    <Button
                        className={cls.button}
                        onClick={handleReset}
                        title={t('filters.reset')}
                        variant="secondary"
                    />
                    <Button
                        className={cls.button}
                        htmlType="submit"
                        title={t('filters.search')}
                        variant="primary"
                    />
                </div>
            </Form>
        );
    },
);

PatternFilterForm.displayName = 'PatternFilterForm';
